import { useState, useCallback } from 'react';
import Button from '@monofront/design-system/components/Button';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import { ICta3Cards } from './types';
import { RichText } from 'prismic-reactjs';
import CustomPrismicLink from '@components/CustomPrismicLink';
import Link from 'next/link';
import withSlice from '@screens/SlicesContainer/withSlice';
import { mapCta3Cards } from './mapCta3Cards';

export interface ICta3CardsProps {
  contents: ICta3Cards[];
}

const Cta3Cards: React.FC<ICta3CardsProps> = ({ contents }) => {
  const [paragraphHeight, setParagraphHeight] = useState(undefined);

  // on load, check the height of all paragraphs inside .footer and set the height of the highest to all other paragraphs
  // useCallback pattern: https://reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
  const cardsRef = useCallback((node) => {
    if (node) {
      const paragraphs = node?.querySelectorAll('.footer p');
      const highestParagraphHeight = [...paragraphs]
        .map((p) => p.clientHeight)
        .sort((a, b) => b - a)[0];

      setParagraphHeight(highestParagraphHeight);
    }
  }, []);

  return (
    <div className="row">
      <div className="row-cta" ref={cardsRef}>
        {contents.map((content: ICta3Cards, index: number) => {
          return (
            <div key={index} className="col-cta">
              <div className="card-cta">
                <h2>{content.title}</h2>
                <div className="card-cta-img">
                  <ResponsiveImage
                    src={content.image}
                    alt={content.alt}
                    layout="fill"
                    objectFit="cover"
                  />
                </div>
                <div className="footer">
                  <RichText
                    render={content.description}
                    serializeHyperlink={CustomPrismicLink}
                  />
                  {content.isExternalLink && content.link ? (
                    <a href={content.link} target="_blank" rel="noreferrer">
                      <Button>{content.buttonLabel}</Button>
                    </a>
                  ) : (
                    content.link && (
                      <Link href={content.link} legacyBehavior>
                        <a>
                          <Button>{content.buttonLabel}</Button>
                        </a>
                      </Link>
                    )
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <style jsx>{`
        .row-cta {
          display: flex;
          flex-wrap: wrap;
        }

        .col-cta {
          flex: 1;
          text-align: center;
        }

        .col-cta > :global(div) {
          height: 100%;
        }

        .card-cta {
          padding: var(--space-5) var(--space-8);
          display: flex;
          flex-direction: column;
          height: 100%;
          align-items: center;
        }

        .card-cta > :global(p) {
          margin-bottom: 22px;
        }

        .card-cta h2 {
          margin-bottom: auto;
        }

        .card-cta-img {
          margin: var(--space-5) auto;
          position: relative;
          width: 100%;
          min-height: 150px;
          max-width: 150px;
        }

        .footer {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          align-items: center;
        }

        .footer > :global(a) {
          margin-top: var(--space-3);
        }

        .footer :global(button) {
          width: initial;
        }

        .footer :global(p:first-of-type) {
          min-height: ${paragraphHeight}px;
        }

        @media (max-width: 800px) {
          .row {
            padding: var(--padding-layout-sm);
          }

          .row-cta {
            flex-direction: column;
          }

          .col-cta {
            margin-bottom: 20px;
          }

          .card-cta {
            padding: var(--space-5) 0;
          }
        }
      `}</style>
    </div>
  );
};

export default withSlice(Cta3Cards, mapCta3Cards);
